import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { TooltipShield } from '@/components/admin/tooltip-shield';
import { useVariableFiche } from '@/components/admin/variables/use-variable-fiche';
import { VariableFiche } from '@/components/admin/variables/variable-fiche';
import { VariableMenu } from '@/components/admin/variables/variable-menu';
import { paintTokensInto } from '@/components/admin/variables/variable-pill';
import type { PlainRange } from '@/lib/rich-text-editor';
import {
  activeColor,
  activeMarks,
  atQueryBefore,
  insertText,
  plainOf,
  plainRangeOf,
  readRichText,
  scanEditable,
  selectPlainRange,
  setColor,
  toggleBulletList,
  toggleMark,
} from '@/lib/rich-text-editor';
import { cn } from '@/lib/utils';
import type { PaletteContent } from '@/lib/variable-palette';
import type { VariableDescriber, VariableZone } from '@/lib/variable-pill';
import { pillOf, variableOfPillToken } from '@/lib/variable-pill';
import type { RichText, RichTextMark, TextColorToken } from '@matter_extractor/shared';
import {
  EXPERIENCE_TEXT_MAX,
  experienceVariablesOf,
  renderRichText,
} from '@matter_extractor/shared';
import { Icon, Tooltip } from '@pms-digital/design-system';
import type { ClipboardEvent as ReactClipboardEvent, ReactNode } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * Le bloc Texte en WYSIWYG (refonte modulaire §9.2, H28, H29; planche
 * `Inspecteurs`; issue #58 ; la pastille de §8.6 depuis l'issue #88).
 *
 * « On voit les mots en gras, en italique, soulignés ou colorés, jamais des
 * marques. » So the surface is a `contentEditable` painted by `renderRichText`
 * - the very function the reader paints with - and the toolbar is B, I, U, the
 * three theme colours, the bullet list and the `{ }` of §8.6. The alignment
 * button of the maquette sits in the Visuel tab, which already carries that
 * token: « un champ montré deux fois n'est pas deux champs stockés deux fois »
 * (`blockVisualSchema`).
 *
 * ## Aucune dépendance, et pourquoi elle n'était pas nécessaire
 *
 * §9.2 closes the format on paragraphs, bullet lists, three marks, three
 * colours and line breaks - and H29 chose a small JSON tree over Markdown. A
 * rich-text library exists to give what is NOT in that list: tables, images,
 * embeds, collaborative cursors, a plugin system. What is left is a model, a
 * renderer that already exists in `shared`, and a selection expressed in plain
 * characters - which is `lib/rich-text-editor.ts`, pure and tested without a
 * DOM. Pulling in ProseMirror or Lexical would have added a runtime an order of
 * magnitude larger than the feature, plus a schema written in ITS language that
 * would then be the second spelling of §9.2.
 *
 * ## React ne possède pas les enfants, et le caret survit par un NOMBRE
 *
 * The div is rendered EMPTY and painted imperatively, the régime
 * `variables/painted-text-field.tsx` states: React does not reconcile a
 * `contentEditable` without moving the caret, so a repaint on every keystroke
 * would drop it at the start of the text node it just rebuilt.
 *
 * Two kinds of change therefore reach this field, and only one repaints.
 *
 *   - **Typing.** The browser has already done it. `onInput` reads the model
 *     back out of the DOM, remembers that signature as what is PAINTED, and
 *     hands the model up. The effect then sees its own signature and paints
 *     nothing, so the caret is never touched.
 *   - **A toolbar press, a paste, a variable.** The model changes without the
 *     DOM. The effect repaints from `renderRichText` and puts the selection
 *     back at the two PLAIN OFFSETS it recorded before the change - which is
 *     the whole reason every operation of `rich-text-editor.ts` speaks in
 *     offsets rather than in nodes.
 *
 * ## La pastille est peinte APRÈS le rendu, sur le DOM du champ (D5)
 *
 * `renderRichText` stays pure and untouched - it escapes, so nothing it emits
 * can be a pill - and leaves each token as its stored spelling in a text node.
 * `paintTokensInto` then turns those text nodes into pills, inside whatever
 * run they sit in, and `scanEditable` reads a pill back as its token in the
 * marks of that run: a variable in a bold sentence stays bold through a round
 * trip, and the plain offsets keep counting the TOKEN, as `richTextPlain` does.
 * The signature the field compares on carries the pills' labels, so a variable
 * renamed in another block repaints this one.
 *
 * ## Le collage est refusé à la porte
 *
 * `onPaste` cancels and reinserts `text/plain`. The reader of the DOM would
 * have kept only the words anyway, but it would have kept the pasted document's
 * ELEMENTS on screen until the next repaint - so the formatting would appear,
 * then vanish, and nobody could tell which of the two was the bug.
 *
 * ## La barre est fermée sur un caret seul
 *
 * B, I and U apply to a SELECTION: a caret carries no passage to mark, and a
 * « pending mark » that survived until the next keystroke would have to live
 * through the browser's own typing, which this file deliberately does not
 * intercept. So the three buttons are disabled with a `title` that says to
 * select first, rather than silently doing nothing.
 */

export interface RichTextFieldProps {
  readonly value: RichText;
  readonly readOnly: boolean;
  /**
   * The `@` menu, for the letters typed after the `@` - the empty string when
   * the `{ }` button opened it. The field reads the query off the text before
   * the caret and stays the only thing that knows where the caret is; the
   * caller filters (`variablePalette`), which stays pure and testable.
   *
   * With `zone`, OPTIONAL since issue #148: a text that takes no variable -
   * the texts of the noyaux, shared by every experience and so in the reach of
   * none (noyau.md §6.2) - passes neither, and the field has no `{ }`, no `@`
   * menu, no pill and no fiche. The format and its renderer stay the Texte
   * block's: that is the whole reason the same field is mounted.
   */
  readonly palette?: (query: string) => PaletteContent;
  /** What each token IS here, for the pill and its fiche (D5, D6), and where « Ouvrir » lands. */
  readonly zone?: VariableZone;
  readonly onChange: (next: RichText) => void;
  /** The field's name, « Texte » by default - the Texte block's (#148: « Premier texte »). */
  readonly label?: string;
  /**
   * The three marks of the field, the page editor's by default. A screen that
   * mounts several fields names each one (#148: eighteen on one tab).
   */
  readonly testIds?: {
    readonly toolbar: string;
    readonly text: string;
    readonly count: string;
  };
}

const COLOR_LABEL: Readonly<Record<TextColorToken, string>> = {
  text: 'Texte',
  muted: 'Texte discret',
  accent: 'Accent',
};

/**
 * Le « A » de la barre, peint dans l'encre du jeton (planche `Inspecteurs`).
 *
 * A letter and not a filled square, and not only because the plate draws one:
 * a swatch answers « what colour is this », a coloured A answers « what will my
 * text look like », which is the question. It also keeps the charte's guard
 * honest - a filled square in one of the dark greys is a SURFACE grounded in
 * it, which `design-system-adherence.test.ts` refuses outright.
 */
const COLOR_INK: Readonly<Record<TextColorToken, string>> = {
  text: 'text-gray-800',
  muted: 'text-gray-500',
  accent: 'text-bleu-nuit',
};

const TOOL =
  'inline-flex size-8 cursor-pointer items-center justify-center rounded-md border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:opacity-50';

/**
 * What is on screen, as one string: the markup, plus what each pill says - and
 * no pill at all for a field that takes no variable (#148).
 */
function paintedSignature(value: RichText, describe: VariableDescriber | undefined): string {
  const pills =
    describe === undefined
      ? []
      : experienceVariablesOf(plainOf(value)).map((variable) => pillOf(describe(variable)));
  return JSON.stringify([renderRichText(value), pills]);
}

const DEFAULT_TEST_IDS = {
  toolbar: PAGE_EDITOR_IDS.richToolbar,
  text: PAGE_EDITOR_IDS.richText,
  count: PAGE_EDITOR_IDS.richCount,
} as const;

export function RichTextField({
  value,
  readOnly,
  palette,
  zone,
  onChange,
  label: fieldLabel = 'Texte',
  testIds = DEFAULT_TEST_IDS,
}: RichTextFieldProps) {
  const describe = zone?.describe;
  /** Whether this field takes variables at all: both halves, or neither (#148). */
  const variables = palette !== undefined && zone !== undefined;
  const host = useRef<HTMLDivElement>(null);
  /** What the DOM currently shows, as the signature that put it there. */
  const painted = useRef<string | null>(null);
  /** Where the selection has to land after the next repaint, or null. */
  const restore = useRef<PlainRange | null>(null);
  const [range, setRange] = useState<PlainRange>({ from: 0, to: 0 });
  /**
   * The `@query` being typed, or null when the menu was opened by `{ }`.
   *
   * Two states and not one boolean, because only the first has something to
   * SWALLOW: the `@` and the letters after it were typed to find a variable,
   * while the `{ }` button consumed nothing of the page. Merged, inserting from
   * `{ }` would have eaten the character before the caret.
   */
  const [at, setAt] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const labelId = useId();
  const fiche = useVariableFiche();

  useEffect(() => {
    const node = host.current;
    if (node === null) return;
    const signature = paintedSignature(value, describe);
    if (painted.current === signature) return;
    /*
      Le SEUL `innerHTML` de ce sous-arbre, et ce qu'il reçoit ne vient jamais
      d'une saisie : `renderRichText` prend un ARBRE de nœuds connus et échappe
      chaque caractère de texte qu'il émet - `&`, `<`, `>`, `"` et `'` - donc il
      n'existe aucun chemin d'une frappe vers un élément. Le test
      « n'ouvre jamais un élément depuis le texte saisi » le tient. Les
      pastilles sont posées ENSUITE, sur les nœuds de texte (D5).
    */
    node.innerHTML = renderRichText(value);
    // No zone, no pill: a field without variables leaves a token as the text
    // it is (#148).
    if (describe !== undefined) paintTokensInto(node, describe);
    painted.current = signature;
    const wanted = restore.current;
    restore.current = null;
    if (wanted !== null) {
      selectPlainRange(node, wanted);
      node.focus();
    }
  }, [value, describe]);

  /** The live selection, in plain characters - null when the caret is elsewhere. */
  function liveRange(): PlainRange | null {
    const node = host.current;
    if (node === null) return null;
    return plainRangeOf(node, scanEditable(node));
  }

  function remember(): void {
    const live = liveRange();
    if (live !== null) setRange(live);
  }

  /**
   * One change the DOM has NOT made: repaint, then put the caret back.
   *
   * A gesture that changed NOTHING returns early rather than arming the
   * restore: the effect would not repaint - the markup is the one already
   * painted - so the order would sit in the ref and fire on whatever repaint
   * came next, moving a caret nobody touched. `toggleBulletList` on an empty
   * document is the one gesture that reaches here having done nothing.
   */
  function apply(next: RichText, caret: PlainRange): void {
    if (next === value) return;
    restore.current = caret;
    setRange(caret);
    onChange(next);
  }

  function onInput(): void {
    const node = host.current;
    if (node === null) return;
    const next = readRichText(node);
    // The DOM is already right, so the effect must not repaint over the caret.
    painted.current = paintedSignature(next, describe);
    const live = plainRangeOf(node, scanEditable(node));
    if (live !== null) setRange(live);
    onChange(next);

    // « Tape @ pour insérer une variable » - read off the text BEFORE the caret
    // rather than from the key, so a paste and an arrow key answer the same.
    // A field that takes no variable has no menu for an `@` to open (#148).
    const typed =
      live === null || !variables ? null : atQueryBefore(plainOf(next).slice(0, live.from));
    setAt(typed);
    if (typed !== null) setMenu(true);
  }

  function onPaste(event: ReactClipboardEvent<HTMLDivElement>): void {
    event.preventDefault();
    if (readOnly) return;
    const text = event.clipboardData.getData('text/plain');
    if (text === '') return;
    const live = liveRange() ?? range;
    const { document: next, caret } = insertText(value, live, text);
    apply(next, { from: caret, to: caret });
  }

  function insertToken(token: string): void {
    const live = liveRange() ?? range;
    /*
      The `@query` the menu was opened on goes WITH the token: the operator
      typed it to find the variable, not to keep it in the page.
    */
    const eaten = at === null ? 0 : at.length + 1;
    const from = Math.max(0, live.from - eaten);
    const { document: next, caret } = insertText(value, { from, to: live.to }, token);
    setMenu(false);
    setAt(null);
    apply(next, { from: caret, to: caret });
  }

  const collapsed = range.from >= range.to;
  const marks = activeMarks(value, range);
  const color = activeColor(value, range);
  const count = plainOf(value).length;

  const standing = useMemo(() => {
    if (fiche.anchor === null) return null;
    const variable = variableOfPillToken(fiche.anchor.token);
    return variable === null || describe === undefined ? null : describe(variable);
  }, [fiche.anchor, describe]);

  /** One of B, I, U: pressed state, its bubble, and the closed caret case. */
  function markTool(mark: RichTextMark, icon: string, label: string): ReactNode {
    const on = marks.includes(mark);
    return (
      <Tooltip key={mark} content={collapsed ? `${label} - sélectionne d’abord du texte` : label}>
        <TooltipShield closed={readOnly || collapsed}>
          <button
            type="button"
            aria-pressed={on}
            aria-label={label}
            disabled={readOnly || collapsed}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => apply(toggleMark(value, range, mark), range)}
            className={cn(
              TOOL,
              on
                ? 'border-bleu-nuit bg-bleu-nuit text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400',
            )}
          >
            <Icon name={icon} size={16} />
          </button>
        </TooltipShield>
      </Tooltip>
    );
  }

  return (
    <div className="space-y-1.5">
      <p id={labelId} className="font-semibold text-gray-800 text-small">
        {fieldLabel}
      </p>

      <div
        data-testid={testIds.toolbar}
        className="flex flex-wrap items-center gap-1 rounded-md border-2 border-gray-200 bg-gray-50 p-1"
      >
        {markTool('bold', 'format_bold', 'Gras')}
        {markTool('italic', 'format_italic', 'Italique')}
        {markTool('underline', 'format_underlined', 'Souligné')}

        <span aria-hidden className="mx-1 h-5 w-px bg-rail" />

        {(['text', 'muted', 'accent'] as const).map((token) => (
          <Tooltip
            key={token}
            content={
              collapsed
                ? `${COLOR_LABEL[token]} - sélectionne d’abord du texte`
                : COLOR_LABEL[token]
            }
          >
            <TooltipShield closed={readOnly || collapsed}>
              <button
                type="button"
                aria-pressed={color === token}
                aria-label={`Couleur : ${COLOR_LABEL[token]}`}
                disabled={readOnly || collapsed}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => apply(setColor(value, range, color === token ? null : token), range)}
                className={cn(
                  TOOL,
                  color === token ? 'border-bleu-nuit' : 'border-gray-200 hover:border-gray-400',
                  'bg-white',
                )}
              >
                <span
                  aria-hidden
                  className={cn('font-bold text-small leading-none', COLOR_INK[token])}
                >
                  A
                </span>
              </button>
            </TooltipShield>
          </Tooltip>
        ))}

        <span aria-hidden className="mx-1 h-5 w-px bg-rail" />

        <Tooltip content="Liste à puces">
          <button
            type="button"
            aria-label="Liste à puces"
            disabled={readOnly}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => apply(toggleBulletList(value, range), range)}
            className={cn(TOOL, 'border-gray-200 bg-white text-gray-700 hover:border-gray-400')}
          >
            <Icon name="format_list_bulleted" size={16} />
          </button>
        </Tooltip>

        {variables ? (
          <Tooltip content="Insérer une variable">
            <button
              type="button"
              aria-label="Insérer une variable"
              disabled={readOnly}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setAt(null);
                setMenu((was) => !was);
              }}
              className={cn(TOOL, 'border-gray-200 bg-white text-gray-700 hover:border-gray-400')}
            >
              <Icon name="data_object" size={16} />
            </button>
          </Tooltip>
        ) : null}
      </div>

      {/*
        `tabIndex` is explicit even though a `contentEditable` is focusable on
        its own: read-only, it is NOT - and the state made for READING must stay
        reachable and selectable, the rule `criterion-editor-fields.tsx` states
        for every field of this application.
      */}
      <div
        ref={host}
        role="textbox"
        tabIndex={0}
        aria-multiline="true"
        aria-labelledby={labelId}
        aria-readonly={readOnly}
        data-testid={testIds.text}
        // A `<textarea>` cannot hold a mark, so the textbox is a div. React
        // renders it EMPTY and the effect paints it - see the head of the file.
        contentEditable={!readOnly}
        suppressContentEditableWarning
        // The fiche of D6, delegated: the pills are rebuilt on every repaint.
        {...fiche.handlers}
        onInput={onInput}
        onPaste={onPaste}
        onKeyUp={remember}
        onMouseUp={remember}
        onBlur={(event) => {
          fiche.handlers.onBlur(event);
          remember();
        }}
        /*
          The painted elements are styled by DESCENDANT variants and not by a
          stylesheet: the model holds six of them and no more, so the list is
          closed and lives beside the field that paints it. The three colours
          are the admin's inks here and the theme's in the preview - this is a
          form, and a passage marked « accent » has to stay legible on white.
        */
        className="min-h-32 w-full rounded-md border-2 border-gray-300 bg-white px-3 py-2 text-gray-800 text-small leading-6 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit focus-visible:ring-offset-1 [&_[data-color=accent]]:text-bleu-nuit [&_[data-color=muted]]:text-gray-500 [&_[data-color=text]]:text-gray-800 [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5"
      />

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {variables ? (
          <span className="text-gray-600 text-caption leading-4">
            Tape @ pour insérer une variable
          </span>
        ) : null}
        <span
          data-testid={testIds.count}
          className={cn(
            // Alone on its row when there is no hint to its left: to the right,
            // where the Texte block keeps it. Untouched when the hint is there.
            !variables && 'ml-auto',
            'text-caption leading-4',
            count > EXPERIENCE_TEXT_MAX ? 'font-semibold text-rouge-deep' : 'text-gray-500',
          )}
        >
          {count} / {EXPERIENCE_TEXT_MAX} caractères
        </span>
      </div>

      {menu && !readOnly && palette !== undefined ? (
        <VariableMenu content={palette(at ?? '')} query={at} onInsert={insertToken} />
      ) : null}

      {fiche.anchor !== null && standing !== null && zone !== undefined ? (
        <VariableFiche
          standing={standing}
          place={zone.place}
          experienceId={zone.experienceId}
          anchor={fiche.anchor.element}
          pinned={fiche.pinned}
          ficheRef={fiche.ficheRef}
        />
      ) : null}
    </div>
  );
}
